import { FlxBitmapText, FlxInputText, FlxState } from '../../../src';
import {
  KenneyValueBar,
  KENNEY_BUTTON_W,
  makeKenneyButton,
  makeKenneyPanelInset,
  requireKenneyUiAtlas,
  requireKenneyUiFont,
} from './kenney-ui';
import { requireMultiPageUiFont } from './multi-page-font';

export interface UiDemoSnapshot {
  damageDisabled: boolean;
  healDisabled: boolean;
  health: number;
  mana: number;
  percent: number;
  playerName: string;
  submittedName: string;
  multiPageFontFamily: string;
}

const PANEL_W = 380;
const PANEL_H = 228;
const PANEL_LEFT = 130;
const PANEL_TOP = 48;
const PANEL_PAD = 16;
const BUTTON_GAP = 8;
const BAR_WIDTH = 300;
const BAR_X = PANEL_LEFT + (PANEL_W - BAR_WIDTH) * 0.5;
const DAMAGE_BUTTON_X = PANEL_LEFT + PANEL_PAD;
const HEAL_BUTTON_X = DAMAGE_BUTTON_X + KENNEY_BUTTON_W + BUTTON_GAP;

export class UiDemoState extends FlxState {
  health = 65;
  mana = 40;
  submittedName = '';

  readonly #healthText = new FlxBitmapText(
    0,
    0,
    '',
    requireKenneyUiFont(),
    BAR_WIDTH,
  );
  readonly #manaText = new FlxBitmapText(
    0,
    0,
    '',
    requireKenneyUiFont(),
    BAR_WIDTH,
  );
  readonly #healthBar = new KenneyValueBar(
    requireKenneyUiAtlas(),
    BAR_X,
    108,
    BAR_WIDTH,
    this,
    'health',
    'barGreen',
  );
  readonly #manaBar = new KenneyValueBar(
    requireKenneyUiAtlas(),
    BAR_X,
    142,
    BAR_WIDTH,
    this,
    'mana',
    'barBlue',
  );
  readonly #damageButton = makeKenneyButton(
    requireKenneyUiAtlas(),
    DAMAGE_BUTTON_X,
    182,
    'Damage',
    () => {
      this.health = Math.max(0, this.health - 15);
    },
    'beige',
  );
  readonly #healButton = makeKenneyButton(
    requireKenneyUiAtlas(),
    HEAL_BUTTON_X,
    182,
    'Heal',
    () => {
      this.health = Math.min(100, this.health + 15);
    },
    'blue',
  );
  readonly #nameInput = new FlxInputText(220, 239, 200, '', {
    accessibleLabel: 'Player name',
    maxLength: 20,
    placeholder: 'Player name',
  });

  override create(): void {
    super.create();
    const atlas = requireKenneyUiAtlas();
    const panel = makeKenneyPanelInset(
      atlas,
      PANEL_LEFT,
      PANEL_TOP,
      PANEL_W,
      PANEL_H,
    );
    panel.scrollFactor.make(0, 0);

    const title = new FlxBitmapText(
      PANEL_LEFT + 16,
      PANEL_TOP + 12,
      'RPG UI PACK',
      requireKenneyUiFont(),
      PANEL_W - 32,
    );
    title.setFormat(null, 0, 0x3d2f24, 'center');
    title.scrollFactor.make(0, 0);

    const multiPageLabel = new FlxBitmapText(
      PANEL_LEFT,
      288,
      'AB AB AB AB',
      requireMultiPageUiFont(),
      PANEL_W,
    );
    multiPageLabel.setFormat(null, 0, 0xe2e8f0, 'center');
    multiPageLabel.scrollFactor.make(0, 0);

    this.#healthText.setFormat(null, 0, 0x4a3b30, 'left');
    this.#healthText.origin.make(0, 0);
    this.#healthText.x = BAR_X;
    this.#healthText.y = 88;
    this.#healthText.scrollFactor.make(0, 0);

    this.#manaText.setFormat(null, 0, 0x4a3b30, 'left');
    this.#manaText.origin.make(0, 0);
    this.#manaText.x = BAR_X;
    this.#manaText.y = 126;
    this.#manaText.scrollFactor.make(0, 0);

    for (const sprite of [
      ...this.#healthBar.sprites,
      ...this.#manaBar.sprites,
    ]) {
      sprite.scrollFactor.make(0, 0);
    }
    this.#healthBar.bar.scrollFactor.make(0, 0);
    this.#manaBar.bar.scrollFactor.make(0, 0);
    this.#damageButton.scrollFactor.make(0, 0);
    this.#healButton.scrollFactor.make(0, 0);

    this.#damageButton.accessibleLabel = 'Damage health by 15';
    this.#damageButton.tabIndex = 0;
    this.#healButton.accessibleLabel = 'Heal health by 15';
    this.#healButton.tabIndex = 0;
    this.#nameInput.setFormat('Arial', 12, 0xf8fafc, 'left');
    this.#nameInput.onSubmit = (value) => {
      this.submittedName = value;
    };

    this.add(panel);
    this.add(title);
    this.add(multiPageLabel);
    this.add(this.#healthText);
    this.add(this.#manaText);
    for (const sprite of this.#healthBar.sprites) this.add(sprite);
    for (const sprite of this.#manaBar.sprites) this.add(sprite);
    this.add(this.#damageButton);
    this.add(this.#healButton);
    this.add(this.#nameInput);
    this.add(this.#healthBar.bar);
    this.add(this.#manaBar.bar);
    this.#syncUi();
  }

  override update(): void {
    super.update();
    this.#syncUi();
  }

  snapshot(): UiDemoSnapshot {
    return {
      damageDisabled: !this.#damageButton.enabled,
      healDisabled: !this.#healButton.enabled,
      health: this.health,
      mana: this.mana,
      multiPageFontFamily: requireMultiPageUiFont().fontFamily,
      percent: this.#healthBar.bar.percent,
      playerName: this.#nameInput.text,
      submittedName: this.submittedName,
    };
  }

  #syncUi(): void {
    this.#damageButton.enabled = this.health > 0;
    this.#healButton.enabled = this.health < 100;
    this.#healthText.text = `Health ${this.health}`;
    this.#manaText.text = `Mana ${this.mana}`;
    this.#healthBar.syncFill();
    this.#manaBar.syncFill();
  }
}
